import { withTransaction } from '@elastic/apm-rum-react'
import {
  Button,
  ButtonBack,
  DakenService,
  Input,
  InputPassword,
  JessicaJonesService,
  LPList,
  Loader,
  LogService,
  MySquid,
  localForageHelper,
  onConfigChange,
  useMessage,
} from '@hub/shared'
import { LandingPage } from '@hub/shared/interfaces/user.interface'
import ErrorMessage from '@src/enums/error-message.enum'
import useDoLogin from '@src/hooks/use-do-login/use-do-login.hook'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { SubscriptionLike } from 'rxjs'

import './bind.page.scoped.scss'
import './bind.page.scss'

const BindPageComponent = () => {
  const jessica = useMemo(() => new JessicaJonesService(), [])
  const dakenService = useMemo(() => new DakenService(), [])
  const logService = useMemo(() => new LogService(), [])
  const { search } = useLocation()
  const [searchParams] = useSearchParams()
  logService.init('BindPage')

  const [loading, setLoading] = useState<boolean>(false)
  const [email, setEmail] = useState<string>(history?.state?.usr?.email || '')

  const [password, setPassword] = useState<string>('')
  const [profileId, setProfileId] = useState<string>('')
  const [squidWhitelabels, setSquidWhitelabels] = useState<LandingPage[]>([])
  const [moreWhitelabels, setMoreWhitelabels] = useState<number>(0)
  const [validPassword, setValidPassword] = useState<boolean>(false)
  const [config, setConfig] = useState<MySquid | null>(null)
  const onConfigChangeSubscription = useRef<SubscriptionLike | undefined>(undefined)
  const [provider] = useState(history.state?.usr?.provider || 'email')
  const [token, setToken] = useState('')
  const { showMessage } = useMessage(ErrorMessage, 'login')

  const { doLogin, removeUtms } = useDoLogin({ email, password })
  const { t } = useTranslation('bindPage')
  const navigate = useNavigate()

  const createBind = useCallback(
    async (event: any): Promise<void> => {
      event.preventDefault()
      try {
        setLoading(true)
        let response
        if (email && password) {
          response = await jessica.backendLogin(email, password, '', '', config?.hasControlAccess)
        } else {
          const token = searchParams.get('token')
          if (token && provider) {
            setToken(token)
            response = await jessica.backendLogin('', '', token, provider === 'youtube' ? 'google' : provider, config?.hasControlAccess)
            await localForageHelper?.setItem('email', response?.userInfo?.email)
          }
        }

        if (config?.hasControlAccess) {
          if (
            !response.userInfo ||
            (config?.controlAccess?.type === 'email' && !config?.controlAccess?.users?.includes(response?.userInfo?.email)) ||
            (config?.controlAccess?.type === 'document' && !config?.controlAccess?.users?.includes(response?.userInfo?.document))
          ) {
            throw {
              data: {
                errorDescription: 'not_allowed',
              },
            }
          }
        }

        if (response?.access) {
          const utmMedium = searchParams.get('utm_medium') ?? ''
          const utmSource = searchParams.get('utm_source') ?? ''
          const utmCampaign = searchParams.get('utm_campaign')
          const utmContent = searchParams.get('utm_content')
          const adId = searchParams.get('ad_id')
          const adName = searchParams.get('ad_name')

          await dakenService.postBindWhitelabel({
            profileId,
            registeredFromMedium: utmMedium,
            registeredFromSource: utmSource,
            registeredFromCampaign: utmCampaign,
            registeredFromContent: utmContent,
            registeredFromAdId: adId,
            registeredFromAdName: adName,
          })
          await removeUtms()
          if (email && password) {
            await doLogin()
          } else {
            navigate(`/login${search}`)
          }

          return
        } else if (!response?.error) {
          throw new Error('unexpected')
        }
      } catch (err: any) {
        showMessage(err.data, 'error')
        logService.error({ message: 'Erro ao verificar e-mail', error: err })
        await removeUtms()
        setLoading(false)
        history.pushState({}, '', '/login')
      }
    },
    [
      navigate,
      search,
      searchParams,
      email,
      password,
      config,
      logService,
      profileId,
      doLogin,
      showMessage,
      dakenService,
      jessica,
      provider,
      removeUtms,
    ]
  )

  const getInfluencerProfileWhitealbel = useCallback(async (): Promise<void> => {
    dakenService
      .getProfileWhiteLabel(email)
      .then((data) => {
        const whitelabels = data?.landingPagesInfos

        const hasHub = whitelabels?.findIndex((whitelabel) => whitelabel._id === '')
        let list: LandingPage[] = []

        if (hasHub >= 0) {
          list = [...list, whitelabels[hasHub]]
          whitelabels.splice(hasHub, 1)
        }

        list = [...list, ...whitelabels]

        setSquidWhitelabels(list)
        setMoreWhitelabels(0)

        setProfileId(data.profileId)
      })
      .catch((err) => {
        logService.error({ message: err?.message })
        navigate('/login', { state: { toastMessage: { message: t('unregistered_user'), type: 'error' } } })
      })
      .finally(() => {
        setLoading(false)
      })
  }, [dakenService, email, logService, navigate, t])

  useEffect(() => {
    const getDataInfluencer = async () => {
      await getInfluencerProfileWhitealbel()
    }

    getDataInfluencer().catch((err) => {
      logService.error({ message: err.message })
    })
  }, [getInfluencerProfileWhitealbel, logService])

  useEffect(() => {
    onConfigChangeSubscription.current = onConfigChange.subscribe((configChanged: MySquid | any) => {
      if (configChanged?._id) {
        setConfig(configChanged)
      }
    })
    return () => {
      onConfigChangeSubscription.current?.unsubscribe()
    }
  }, [onConfigChangeSubscription])

  return loading ? (
    <Loader />
  ) : (
    <div>
      <header className="bind-header">
        <ButtonBack className="top-back-bind" onReferrerFalseUrl="/login" />
      </header>
      <h4 className="main-title">{t('title')}</h4>
      <div className="subtitle mb-5">{t('subtitle')}</div>
      <LPList
        squidWhitelabels={squidWhitelabels}
        setSquidWhitelabels={setSquidWhitelabels}
        moreWhitelabels={moreWhitelabels}
        setMoreWhitelabels={setMoreWhitelabels}
      />
      <div className="divider"></div>
      <h4 className="title mt-4">{t('instructions')}</h4>
      <form onSubmit={createBind} style={{ width: '100%' }}>
        <Input
          label={t('globalLogin:email')}
          type="email"
          placeholder="email@email.com"
          value={email}
          name="bind-email"
          id="bind-email"
          className="mb-3"
          onChange={(email: string) => setEmail(email.trim())}
          readOnly
          loading={loading}
          autoComplete="email"
          required
        />
        {provider === 'email' ? (
          <InputPassword
            label={
              <span>
                {t('globalLogin:password')}{' '}
                <Link
                  className="forget-password"
                  state={{ prevPath: location.origin }}
                  to={{ pathname: '/login/forgot-password', search: search.toString() }}
                >
                  {t('forgotPassword')}
                </Link>
              </span>
            }
            value={password}
            name="bind-password"
            id="bind-password"
            className="mb-3"
            placeholder="********"
            onChange={(password: string) => setPassword(password.trim())}
            onValidate={(password: boolean) => setValidPassword(password)}
            readOnly={loading}
            loading={loading}
            autoComplete="current-password"
            required
          />
        ) : null}
        <p className="mb-5 text-center" style={{ fontSize: '0.9rem' }} dangerouslySetInnerHTML={{ __html: t('contract') }} />
        <Button
          loading={loading}
          type="submit"
          disabled={provider === 'email' && !validPassword && !loading}
          className="button-login"
          size="xl"
          id="button-bind"
        >
          {t('activate')}
        </Button>
        <p className="mt-4 text-center" dangerouslySetInnerHTML={{ __html: t('registrationAlert') }} />
      </form>
    </div>
  )
}

export default withTransaction('BindPageComponent', 'component')(BindPageComponent)
